interface ReconcileResult {
  reconciled: boolean;
  reason?: string;
  /**
   * Sections whose stored answer the refreshed field no longer accepts, which the run therefore
   * emptied. The server sends the names because nothing else in the response records the
   * deletion: narrowing a field adds and retires nothing, so the sections either side of the run
   * are identical and every count below reads zero.
   */
  clearedSections?: ReadonlyArray<{ id: string; name: string }>;
}

interface SectionSnapshot {
  id: string;
  retired: boolean;
}

export type ReconcileOutcome =
  | { kind: 'changed'; added: number; retired: number; cleared: readonly string[] }
  | { kind: 'cleared'; cleared: readonly string[] }
  | { kind: 'nothing-to-do' }
  | { kind: 'no-template' }
  | { kind: 'template-unavailable' };

/**
 * What to tell the person who asked the brief to check its template.
 *
 * What was added or retired is read from the brief's own sections either side of the run, because
 * the endpoint does not count them. Retirement is counted as a transition rather than as a state,
 * so asking twice does not report the same field twice. What was DELETED cannot be read that way
 * at all and is taken from the endpoint, which is the only party that knows.
 *
 * A refusal is never reported as success. `no_template` is a settled fact about this brief;
 * anything else means the template could not be read and nothing was written, which leaves the
 * question open rather than answered.
 */
export function describeReconcileOutcome(
  result: ReconcileResult,
  before: readonly SectionSnapshot[],
  after: readonly SectionSnapshot[],
): ReconcileOutcome {
  if (!result.reconciled) {
    return result.reason === 'no_template'
      ? { kind: 'no-template' }
      : { kind: 'template-unavailable' };
  }

  const knownIds = new Set(before.map((section) => section.id));
  const retiredBefore = new Set(
    before.filter((section) => section.retired).map((section) => section.id),
  );

  const added = after.filter((section) => !knownIds.has(section.id)).length;
  const retired = after.filter(
    (section) => section.retired && knownIds.has(section.id) && !retiredBefore.has(section.id),
  ).length;
  const cleared = (result.clearedSections ?? []).map((section) => section.name);

  if (added === 0 && retired === 0) {
    return cleared.length === 0 ? { kind: 'nothing-to-do' } : { kind: 'cleared', cleared };
  }

  return { kind: 'changed', added, retired, cleared };
}
